import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { CurrentUser } from "@/lib/adminAuth";

interface UserMenuProps {
  me: CurrentUser;
  onSignOut: () => void;
  signingOut?: boolean;
}

/**
 * Compact top-right user menu: name + role pill collapses into a click-to-open
 * dropdown with Change password + Sign out actions. Closes on outside click and Escape.
 */
export function UserMenu({ me, onSignOut, signingOut }: UserMenuProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const initials = me.displayName
    .split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

  const topRole = me.roles.includes("SuperAdmin")
    ? "SuperAdmin" : me.roles.includes("Admin") ? "Admin" : me.roles[0] ?? "";

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-pill border border-brand-ink/10 bg-white px-2 py-1 pr-3 text-sm hover:bg-brand-light">
        <span className="grid h-8 w-8 place-items-center rounded-full bg-brand text-xs font-bold text-white">
          {initials || "?"}
        </span>
        <span className="hidden text-brand-ink sm:inline">{me.displayName}</span>
        <span className="hidden rounded-pill bg-brand-light px-2 py-0.5 text-[10px] font-semibold text-brand-ink/70 sm:inline">
          {topRole}
        </span>
        <svg className={`h-4 w-4 text-brand-ink/50 transition ${open ? "rotate-180" : ""}`}
          viewBox="0 0 20 20" fill="currentColor" aria-hidden>
          <path d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" />
        </svg>
      </button>

      {open && (
        <div role="menu"
          className="absolute right-0 top-full z-30 mt-2 w-64 overflow-hidden rounded-xl border border-brand-ink/10 bg-white shadow-lg">
          <div className="border-b border-brand-ink/10 px-4 py-3">
            <div className="text-sm font-semibold text-brand-ink">{me.displayName}</div>
            <div className="truncate text-xs text-brand-ink/60">{me.email}</div>
            <div className="mt-2 flex flex-wrap gap-1">
              {me.roles.map((r) => (
                <span key={r} className={`rounded-pill px-2 py-0.5 text-[10px] font-semibold
                  ${r === "SuperAdmin" ? "bg-brand text-white"
                    : r === "Admin" ? "bg-emerald-100 text-emerald-800"
                    : "bg-yellow-100 text-yellow-800"}`}>
                  {r}
                </span>
              ))}
            </div>
          </div>
          <Link to="/admin/change-password" role="menuitem"
            onClick={() => setOpen(false)}
            className="block px-4 py-2 text-sm text-brand-ink hover:bg-brand-light">
            Change password
          </Link>
          <button role="menuitem"
            onClick={() => { setOpen(false); onSignOut(); }}
            disabled={signingOut}
            className="block w-full px-4 py-2 text-left text-sm text-brand hover:bg-brand-light disabled:opacity-50">
            {signingOut ? "Signing out…" : "Sign out"}
          </button>
        </div>
      )}
    </div>
  );
}
